import { GameState, Player, CellState, Position, Move, GameConfig, GameMode, Difficulty } from '../types/game';

// 游戏配置
export const GAME_CONFIG: GameConfig = {
  boardSize: 15,
  initialEnergy: 3,
  energyPerTurn: 1,
  winCondition: 5,
  gameType: 'gomoku',
};

// 创建空棋盘
export const createEmptyBoard = (size: number): CellState[][] => {
  return Array(size).fill(null).map(() => Array(size).fill(null));
};

// 创建初始游戏状态
export const createInitialGameState = (gameMode: GameMode = 'pvp', difficulty?: Difficulty): GameState => {
  return {
    board: createEmptyBoard(GAME_CONFIG.boardSize),
    currentPlayer: 'black',
    status: 'playing',
    winner: null,
    moves: [],
    energy: { black: GAME_CONFIG.initialEnergy, white: GAME_CONFIG.initialEnergy },
    skills: { black: [], white: [] },
    gameHistory: [],
    gameMode,
    difficulty,
    isAIThinking: false,
    gameType: 'gomoku',
  };
};

// 检查位置是否有效
export const isValidPosition = (position: Position, boardSize: number): boolean => {
  return position.row >= 0 && position.row < boardSize && 
         position.col >= 0 && position.col < boardSize;
};

//检查位置是否为空
export const isEmpty = (board: CellState[][], position: Position): boolean => {
  return board[position.row][position.col] === null;
};

// 下棋
export const makeMove = (gameState: GameState, position: Position): GameState => {
  if (gameState.status !== 'playing') return gameState;
  if (!isValidPosition(position, GAME_CONFIG.boardSize) || !isEmpty(gameState.board, position)) {
    return gameState;
  }

  const newBoard = gameState.board.map(row => [...row]);
  newBoard[position.row][position.col] = gameState.currentPlayer;

  const newMove: Move = {
    position,
    player: gameState.currentPlayer,
    timestamp: Date.now(),
  };

  const newEnergy = { ...gameState.energy };
  newEnergy[gameState.currentPlayer] = Math.max(0, 
    newEnergy[gameState.currentPlayer] - GAME_CONFIG.energyPerTurn);

  const newState: GameState = {
    ...gameState,
    board: newBoard,
    currentPlayer: gameState.currentPlayer === 'black' ? 'white' : 'black',
    moves: [...gameState.moves, newMove],
    energy: newEnergy,
    gameHistory: [...gameState.gameHistory, gameState],
  };

  // 检查胜负
  const winner = checkWinner(newBoard, position, gameState.currentPlayer);
  if (winner) {
    newState.status = 'finished';
    newState.winner = winner;
  }

  return newState;
};

// 检查五子连珠
export const checkWinner = (board: CellState[][], lastMove: Position, player: Player): Player | null => {
  const directions = [
    [0, 1],   // 水平
    [1, 0],   // 垂直
    [1, 1],   // 对角线
    [1, -1],  // 反对角线
  ];

  for (const [dx, dy] of directions) {
    let count = 1;

    // 向一个方向检查
    for (let i = 1; i < GAME_CONFIG.winCondition; i++) {
      const newRow = lastMove.row + dx * i;
      const newCol = lastMove.col + dy * i;
      if (!isValidPosition({ row: newRow, col: newCol }, GAME_CONFIG.boardSize) ||
          board[newRow][newCol] !== player) {
        break;
      }
      count++;
    }

    // 向相反方向检查
    for (let i = 1; i < GAME_CONFIG.winCondition; i++) {
      const newRow = lastMove.row - dx * i;
      const newCol = lastMove.col - dy * i;
      if (!isValidPosition({ row: newRow, col: newCol }, GAME_CONFIG.boardSize) ||
          board[newRow][newCol] !== player) {
        break;
      }
      count++;
    }

    if (count >= GAME_CONFIG.winCondition) {
      return player;
    }
  }

  return null;
};

// 悔棋
export const undoMove = (gameState: GameState): GameState => {
  if (gameState.moves.length === 0 || gameState.gameHistory.length === 0) {
    return gameState;
  }

  const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];
  return {
    ...previousState,
    gameHistory: gameState.gameHistory.slice(0, -1),
  };
};

// 重新开始游戏
export const resetGame = (gameMode: GameMode = 'pvp', difficulty?: Difficulty): GameState => {
  return createInitialGameState(gameMode, difficulty);
};

// 增加能量（每回合恢复）
export const addEnergy = (gameState: GameState): GameState => {
  const newEnergy = { ...gameState.energy };
  newEnergy[gameState.currentPlayer] = Math.min(10, 
    newEnergy[gameState.currentPlayer] + GAME_CONFIG.energyPerTurn);
  
  return {
    ...gameState,
    energy: newEnergy,
  };
};